import { useState, useEffect, useCallback } from "react";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  RefreshCw,
  Search,
  ExternalLink,
  Upload,
  Pencil,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  FileText,
  Eye,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogManagementPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [editingPost, setEditingPost] = useState(null);
  const [editData, setEditData] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 12 });
      if (search) params.append("search", search);
      const res = await fetch(`${API}/api/blog-wp/posts?${params}`);
      if (!res.ok) throw new Error("Errore caricamento");
      const data = await res.json();
      setPosts(data.posts);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      toast.error("Errore nel caricamento degli articoli");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const startEdit = async (post) => {
    try {
      const res = await fetch(`${API}/api/blog-wp/posts/${post.id}`);
      if (!res.ok) throw new Error();
      const full = await res.json();
      setEditingPost(full);
      setEditData({
        title: full.title,
        content: full.content,
        excerpt: stripHtml(full.excerpt),
      });
    } catch {
      toast.error("Errore caricamento articolo");
    }
  };

  const saveEdit = async () => {
    if (!editingPost) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/blog-wp/posts/${editingPost.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData),
      });
      if (!res.ok) throw new Error();
      toast.success("Articolo aggiornato!");
      setEditingPost(null);
      fetchPosts();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (postId, file) => {
    setUploadingId(postId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/api/blog-wp/posts/${postId}/image`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Immagine caricata! Media ID: ${data.media_id}`);
      fetchPosts();
    } catch {
      toast.error("Errore upload immagine");
    } finally {
      setUploadingId(null);
    }
  };

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  // Edit Modal
  if (editingPost) {
    return (
      <PageLayout>
        <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="blog-edit-modal">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-xl font-bold text-foreground">Modifica Articolo</h1>
            <Button variant="ghost" size="icon" onClick={() => setEditingPost(null)} data-testid="close-edit-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Titolo</label>
              <Input
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                data-testid="edit-title-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Excerpt</label>
              <textarea
                className="w-full min-h-[80px] rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={editData.excerpt}
                onChange={(e) => setEditData({ ...editData, excerpt: e.target.value })}
                data-testid="edit-excerpt-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Contenuto HTML</label>
              <textarea
                className="w-full min-h-[400px] rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={editData.content}
                onChange={(e) => setEditData({ ...editData, content: e.target.value })}
                data-testid="edit-content-input"
              />
            </div>

            {editingPost.image_url && (
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Immagine attuale</label>
                <img src={editingPost.image_url} alt="" className="h-40 rounded-lg object-cover" />
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Sostituisci immagine</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files[0]) handleImageUpload(editingPost.id, e.target.files[0]);
                }}
                className="text-sm"
                data-testid="edit-image-upload"
              />
              {uploadingId === editingPost.id && (
                <p className="text-xs text-primary mt-1">Caricamento in corso...</p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={saveEdit} disabled={saving} data-testid="save-edit-btn">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Salva Modifiche
              </Button>
              <Button variant="outline" onClick={() => setEditingPost(null)}>
                Annulla
              </Button>
              <a href={editingPost.link} target="_blank" rel="noopener noreferrer" className="ml-auto">
                <Button variant="ghost" size="sm">
                  <Eye className="w-4 h-4 mr-1" /> Vedi su WordPress
                </Button>
              </a>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="p-4 lg:p-8" data-testid="blog-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Blog WordPress</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {total} articoli su odr.ioxina.com/blog
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={async () => {
                if (!window.confirm("Sincronizzare gli ultimi 10 articoli interni su WordPress? (auto-pubblica con immagine + SEO)")) return;
                try {
                  setSyncing(true);
                  const apiUrl = (process.env.REACT_APP_BACKEND_URL || "") + "/api/blog-wp/sync-from-internal?count=10";
                  const res = await fetch(apiUrl, { method: "POST" });
                  const data = await res.json();
                  if (data.success) {
                    alert(`Sync completato!\nCreati: ${data.summary?.created || 0}\nAggiornati: ${data.summary?.updated || 0}\nSaltati: ${data.summary?.skipped || 0}\nErrori: ${data.summary?.errors || 0}`);
                    fetchPosts();
                  } else {
                    alert("Errore durante il sync: " + JSON.stringify(data).substring(0, 200));
                  }
                } catch (e) {
                  alert("Errore: " + e.message);
                } finally {
                  setSyncing(false);
                }
              }}
              disabled={syncing}
              data-testid="sync-wp-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sync in corso..." : "🔄 Sync Blog Interno → WP"}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchPosts} data-testid="refresh-posts-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
            <a href="https://odr.ioxina.com/blog/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="view-blog-btn">
                <ExternalLink className="w-4 h-4 mr-1" /> Vai al Blog
              </Button>
            </a>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6 max-w-md" data-testid="search-form">
          <Input
            placeholder="Cerca articoli..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="search-input"
          />
          <Button type="submit" size="sm" data-testid="search-btn">
            <Search className="w-4 h-4" />
          </Button>
        </form>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-72 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Nessun articolo trovato</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {posts.map((post) => (
              <div
                key={post.id}
                className="group rounded-xl border border-border bg-card overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`post-card-${post.id}`}
              >
                {/* Image */}
                <div className="relative h-44 bg-muted overflow-hidden">
                  {post.image_url ? (
                    <img
                      src={post.image_url}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-10 h-10 text-muted-foreground/30" />
                    </div>
                  )}
                  {/* Upload overlay */}
                  <label
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                    data-testid={`upload-trigger-${post.id}`}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) handleImageUpload(post.id, e.target.files[0]);
                      }}
                    />
                    {uploadingId === post.id ? (
                      <RefreshCw className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <div className="flex flex-col items-center text-white">
                        <Upload className="w-6 h-6 mb-1" />
                        <span className="text-xs font-medium">Cambia immagine</span>
                      </div>
                    )}
                  </label>
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">
                    {new Date(post.date).toLocaleDateString("it-IT", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                    {!post.image_url && (
                      <span className="ml-2 text-amber-500 font-medium">Immagine mancante</span>
                    )}
                  </p>
                  <h3
                    className="font-semibold text-sm leading-snug line-clamp-2 mb-2"
                    dangerouslySetInnerHTML={{ __html: post.title }}
                  />
                  <p
                    className="text-xs text-muted-foreground line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  />
                </div>

                {/* Actions */}
                <div className="px-4 pb-3 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => startEdit(post)}
                    data-testid={`edit-btn-${post.id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Modifica
                  </Button>
                  <a href={post.link} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="ghost" size="sm" className="w-full text-xs" data-testid={`view-btn-${post.id}`}>
                      <ExternalLink className="w-3 h-3 mr-1" /> Vedi
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8" data-testid="pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Pagina {page} di {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
